import React, { useContext, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Link } from 'react-router-dom';
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Card, Empty, Space, Spin, Table, theme, Tooltip } from 'antd';
import type { ApexOptions } from 'apexcharts';

import MaintenanceWindowsV2Context from '@totem/components/maintenanceWindowsV2/MaintenanceWindowsV2Context';
import {
  BypassTag,
  StatusTag,
} from '@totem/components/maintenanceWindowsV2/StatusTags';
import {
  AttentionSeverity,
  MaintenanceWindow,
} from '@totem/components/maintenanceWindowsV2/types';
import {
  approvalResponseMs,
  buildAttentionItems,
  DAY_MS,
  durationHours,
  endTime,
  formatDay,
  formatDuration,
  formatTime,
  getApprovalDecision,
  getBuildingLabel,
  getBypassState,
  getControlSystemNames,
  getWindowStatus,
  HOUR_MS,
  inLookback,
  isAutoApproval,
  isClosed,
  isPendingForUser,
  median,
  percentile,
  startTime,
  toTime,
} from '@totem/components/maintenanceWindowsV2/utilities';
import { WindowsView } from '@totem/components/maintenanceWindowsV2/windows/WindowsTab';
import {
  isDarkBackground,
  sequentialColor,
  trendColors,
} from '@totem/components/software/vulnerabilityReport/summary/chartColors';
import KpiCard from '@totem/components/ui_components/widgets/KpiCard';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

type Props = {
  onDrillDown: (view: WindowsView) => void;
};

const detailLink = (win: MaintenanceWindow) =>
  `/dashboard/maintenanceWindows2/${win.id}`;

const windowName = (win: MaintenanceWindow) =>
  win.details.reason || 'Maintenance window';

const cardStyles = { body: { padding: 12 } };

const twoColumn: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
  gap: 16,
};

const SummaryTab = ({ onDrillDown }: Props) => {
  const { loading, filteredData, filters, setFilters, now, sending, onAction } =
    useContext(MaintenanceWindowsV2Context);
  const { userProfile } = useContext(UserProfileContext);
  const { token } = theme.useToken();
  const isDark = isDarkBackground(token.colorBgContainer);
  const { lookbackDays } = filters;

  const summary = useMemo(() => {
    const windows = filteredData;
    const statusOf = (win: MaintenanceWindow) => getWindowStatus(win, now);
    const history = windows.filter((win) => inLookback(win, now, lookbackDays));

    const active = windows.filter((win) => statusOf(win) === 'active');
    const upcoming = windows.filter(
      (win) =>
        !isClosed(win) &&
        startTime(win) > now &&
        startTime(win) <= now + 7 * DAY_MS,
    );
    const awaiting = windows.filter(
      (win) => statusOf(win) === 'awaitingApproval',
    );
    const awaitingSoon = awaiting.filter(
      (win) => startTime(win) - now < DAY_MS,
    );
    const oldestRequest = Math.min(
      ...awaiting.flatMap((win) =>
        (win.approvals ?? []).map((approval) => toTime(approval.requestedAt)),
      ),
    );
    const openOverlays = windows.filter((win) =>
      ['open', 'notRemoved'].includes(getBypassState(win, now)),
    );
    const notRemoved = openOverlays.filter(
      (win) => getBypassState(win, now) === 'notRemoved',
    );
    const suppressed = history.flatMap((win) => win.blockedEvents ?? []);
    const closedHistory = history.filter(isClosed);
    const closedHours = closedHistory.map(durationHours);
    const deviceHours = closedHistory.reduce(
      (total, win) => total + durationHours(win) * (win.devices ?? []).length,
      0,
    );

    // Now & next: anything not closed that is running, overdue, or starts within 7 days.
    const agenda = windows
      .filter(
        (win) =>
          !isClosed(win) &&
          startTime(win) <= now + 7 * DAY_MS &&
          (endTime(win) >= now || statusOf(win) === 'overdue'),
      )
      .sort((left, right) => startTime(left) - startTime(right));

    // Bypass lifecycle: open/upcoming bypass windows plus those in the history window.
    const bypassWindows = windows.filter(
      (win) =>
        win.details.requiresBypass &&
        (inLookback(win, now, lookbackDays) || !isClosed(win)),
    );
    const bypassHistory = bypassWindows.filter((win) => startTime(win) <= now);
    const bypassStats = {
      requested: bypassHistory.length,
      created: bypassHistory.filter(
        (win) => (win.overlayNetworkBypasses ?? []).length > 0,
      ).length,
      notCreated: bypassHistory.filter(
        (win) => getBypassState(win, now) === 'notCreated',
      ).length,
      rejected: bypassWindows.filter(
        (win) => getBypassState(win, now) === 'rejected',
      ).length,
      openNow: openOverlays.length,
    };
    const issueRank = (win: MaintenanceWindow) =>
      ['notRemoved', 'notCreated'].includes(getBypassState(win, now))
        ? 0
        : statusOf(win) === 'closed'
          ? 2
          : 1;
    const bypassRows = [...bypassWindows]
      .sort(
        (left, right) =>
          issueRank(left) - issueRank(right) ||
          Math.abs(startTime(left) - now) - Math.abs(startTime(right) - now),
      )
      .slice(0, 8);

    // Approvals: window-level outcome plus per-approver response times, for requests in the history window.
    const requestWindows = windows.filter(
      (win) =>
        win.details.requiresBypass &&
        (win.approvals ?? []).some(
          (approval) =>
            toTime(approval.requestedAt) >= now - lookbackDays * DAY_MS,
        ),
    );
    const outcome = { approved: 0, autoApproved: 0, rejected: 0, pending: 0 };
    const responseTimes: number[] = [];
    let lateApprovals = 0;
    const approvers = new Map<
      string,
      {
        pending: number;
        approved: number;
        rejected: number;
        responses: number[];
      }
    >();
    requestWindows.forEach((win) => {
      const winApprovals = win.approvals ?? [];
      const approvedBy = winApprovals.find(
        (approval) => approval.status === 'Approved',
      );
      if (approvedBy) {
        if (isAutoApproval(win, approvedBy)) {
          outcome.autoApproved += 1;
        } else {
          outcome.approved += 1;

          responseTimes.push(approvalResponseMs(approvedBy));
        }
        if (toTime(approvedBy.approvedAt) > startTime(win) - DAY_MS) {
          lateApprovals += 1;
        }
      } else if (
        winApprovals.some((approval) => approval.status === 'Rejected')
      ) {
        outcome.rejected += 1;
      } else if (!isClosed(win)) {
        outcome.pending += 1;
      }
      winApprovals.forEach((approval) => {
        const decision = getApprovalDecision(win, approval);
        if (decision === 'autoApproved' || decision === 'notRequired') {
          return;
        }
        const entry = approvers.get(approval.approver) ?? {
          pending: 0,
          approved: 0,
          rejected: 0,
          responses: [],
        };
        if (decision === 'pending') {
          entry.pending += 1;
        } else {
          entry[decision] += 1;

          entry.responses.push(approvalResponseMs(approval));
        }
        approvers.set(approval.approver, entry);
      });
    });
    const approverRows = [...approvers.entries()]
      .map(([approver, entry]) => ({
        approver,
        pending: entry.pending,
        approved: entry.approved,
        rejected: entry.rejected,
        median: median(entry.responses),
      }))
      .sort(
        (left, right) =>
          right.pending - left.pending || right.approved - left.approved,
      );

    // Monthly volume by bypass outcome, from the start of the history window through this month.
    const monthStart = new Date(now - lookbackDays * DAY_MS);
    monthStart.setDate(1);
    const months: { key: string; label: string }[] = [];
    for (
      const cursor = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth(),
        1,
      );
      cursor.getTime() <= now;
      cursor.setMonth(cursor.getMonth() + 1)
    ) {
      months.push({
        key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
        label: cursor.toLocaleDateString([], {
          month: 'short',
          year: '2-digit',
        }),
      });
    }
    const monthIndex = new Map(
      months.map((month, index) => [month.key, index]),
    );
    const monthly = {
      noBypass: months.map(() => 0),
      bypassApproved: months.map(() => 0),
      bypassNotApproved: months.map(() => 0),
    };
    windows.forEach((win) => {
      const start = new Date(startTime(win));
      const index = monthIndex.get(
        `${start.getFullYear()}-${start.getMonth()}`,
      );
      if (index === undefined) {
        return;
      }
      if (!win.details.requiresBypass) {
        monthly.noBypass[index] += 1;
      } else if (win.details.bypassApproved) {
        monthly.bypassApproved[index] += 1;
      } else {
        monthly.bypassNotApproved[index] += 1;
      }
    });

    // Load by building over the history window.
    const buildingLoad = new Map<
      string,
      { id: string; name: string; windows: number; deviceHours: number }
    >();
    history.forEach((win) => {
      const perBuilding = new Map<string, { name: string; devices: number }>();
      (win.devices ?? []).forEach((dev) => {
        if (!dev.building?.id) {
          return;
        }
        const current = perBuilding.get(dev.building.id) ?? {
          name: dev.building.name,
          devices: 0,
        };
        current.devices += 1;

        perBuilding.set(dev.building.id, current);
      });

      perBuilding.forEach((value, buildingId) => {
        const entry = buildingLoad.get(buildingId) ?? {
          id: buildingId,
          name: value.name,
          windows: 0,
          deviceHours: 0,
        };
        entry.windows += 1;

        entry.deviceHours += durationHours(win) * value.devices;

        buildingLoad.set(buildingId, entry);
      });
    });
    const buildings = [...buildingLoad.values()]
      .sort((left, right) => right.windows - left.windows)
      .slice(0, 10);

    return {
      active,
      upcoming,
      awaiting,
      awaitingSoon,
      oldestRequest,
      openOverlays,
      notRemoved,
      suppressed,
      closedHistory,
      closedHours,
      deviceHours,
      agenda,
      bypassStats,
      bypassRows,
      outcome,
      responseTimes,
      lateApprovals,
      approverRows,
      months,
      monthly,
      buildings,
      attention: buildAttentionItems(windows, now, lookbackDays),
      myDecisions: windows
        .filter((win) => isPendingForUser(win, userProfile.email, now))
        .sort((left, right) => startTime(left) - startTime(right)),
    };
  }, [filteredData, now, lookbackDays, userProfile.email]);

  const chartBase = useMemo(
    (): ApexOptions => ({
      chart: {
        toolbar: { show: false },
        animations: { enabled: false },
        background: 'transparent',
        fontFamily: 'inherit',
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      grid: { borderColor: token.colorBorderSecondary },
      dataLabels: { enabled: false },
      legend: {
        position: 'bottom',
        fontSize: '11px',
        labels: { colors: token.colorTextSecondary },
      },
      tooltip: { theme: isDark ? 'dark' : 'light' },
    }),
    [isDark, token.colorBorderSecondary, token.colorTextSecondary],
  );

  if (loading && filteredData.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const severityIcon = (severity: AttentionSeverity) => {
    switch (severity) {
      case 'critical':
        return (
          <ExclamationCircleOutlined
            style={{ color: token.colorError, fontSize: 16 }}
          />
        );
      case 'warning':
        return (
          <WarningOutlined
            style={{ color: token.colorWarning, fontSize: 16 }}
          />
        );
      default:
        return (
          <InfoCircleOutlined
            style={{ color: token.colorTextSecondary, fontSize: 16 }}
          />
        );
    }
  };

  const rowDivider = `1px solid ${token.colorBorderSecondary}`;
  const secondary = { color: token.colorTextSecondary };
  const sectionLabel: React.CSSProperties = {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: token.colorTextSecondary,
    fontWeight: 600,
  };

  const lifecycleDots = (win: MaintenanceWindow) => {
    const status = getWindowStatus(win, now);
    const bypass = getBypassState(win, now);
    const started = startTime(win) <= now;
    const created = (win.overlayNetworkBypasses ?? []).length > 0;
    const steps: { label: string; color: string; hollow?: boolean }[] = [
      { label: 'Requested', color: token.colorSuccess },
      {
        label:
          bypass === 'rejected'
            ? 'Rejected'
            : win.details.bypassApproved
              ? 'Approved'
              : 'Awaiting approval',
        color:
          bypass === 'rejected'
            ? token.colorError
            : win.details.bypassApproved
              ? token.colorSuccess
              : token.colorWarning,
        hollow: bypass === 'pending',
      },
      {
        label: created
          ? 'Overlay created'
          : started && win.details.bypassApproved
            ? 'Overlay not created'
            : 'Overlay created at start',
        color: created
          ? token.colorSuccess
          : started && win.details.bypassApproved
            ? token.colorError
            : token.colorBorder,
      },
      {
        label:
          status === 'closed'
            ? 'Window closed'
            : status === 'overdue'
              ? 'Window not closed'
              : status === 'active'
                ? 'Window running'
                : 'Closes at end',
        color:
          status === 'closed'
            ? token.colorSuccess
            : status === 'overdue'
              ? token.colorError
              : status === 'active'
                ? token.colorPrimary
                : token.colorBorder,
        hollow: status === 'active',
      },
    ];
    return (
      <Tooltip title={steps.map((step) => step.label).join(' → ')}>
        <span
          role="img"
          aria-label={steps.map((step) => step.label).join(', ')}
          style={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.label}>
              {index > 0 && (
                <span
                  style={{
                    width: 14,
                    height: 2,
                    background: token.colorBorderSecondary,
                  }}
                />
              )}
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  background: step.hollow ? 'transparent' : step.color,
                  border: `2px solid ${step.color}`,
                }}
              />
            </React.Fragment>
          ))}
        </span>
      </Tooltip>
    );
  };

  const { outcome } = summary;
  const outcomeTotal =
    outcome.approved +
    outcome.autoApproved +
    outcome.rejected +
    outcome.pending;
  const outcomeParts = [
    {
      key: 'approved',
      label: 'Approved',
      value: outcome.approved,
      color: token.colorSuccess,
    },
    {
      key: 'autoApproved',
      label: 'Auto-approved',
      value: outcome.autoApproved,
      color: token.colorSuccessBorder,
    },
    {
      key: 'rejected',
      label: 'Rejected',
      value: outcome.rejected,
      color: token.colorError,
    },
    {
      key: 'pending',
      label: 'Pending',
      value: outcome.pending,
      color: token.colorWarning,
    },
  ];

  const trend = trendColors(isDark);
  const monthlyOptions: ApexOptions = {
    ...chartBase,
    chart: { ...chartBase.chart, type: 'bar', stacked: true },
    plotOptions: {
      bar: {
        columnWidth: '55%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { width: 2, colors: [token.colorBgContainer] },
    colors: [trend[0], trend[2], trend[1]],
    xaxis: {
      categories: summary.months.map((month) => month.label),
      labels: { style: { colors: token.colorTextSecondary } },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        style: { colors: token.colorTextSecondary },
        formatter: (value: number) => `${Math.round(value)}`,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: isDark ? 'dark' : 'light',
    },
  };

  const maxBuildingWindows = Math.max(
    0,
    ...summary.buildings.map((building) => building.windows),
  );
  // Whole-number ticks from 0: at most 5 steps, each a whole number of windows.
  const buildingTickStep = Math.max(1, Math.ceil(maxBuildingWindows / 5));
  const buildingTicks = Math.max(
    1,
    Math.ceil(maxBuildingWindows / buildingTickStep),
  );
  const buildingOptions: ApexOptions = {
    ...chartBase,
    chart: {
      ...chartBase.chart,
      type: 'bar',
      events: {
        dataPointSelection: (_event, _chart, config) => {
          const building = summary.buildings[config.dataPointIndex];
          if (building) {
            setFilters({ buildingIds: [building.id] });

            onDrillDown('all');
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '62%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
      },
    },
    colors: [sequentialColor(isDark)],
    legend: { show: false },
    xaxis: {
      categories: summary.buildings.map(
        (building) =>
          `${building.name} (${Math.round(building.deviceHours).toLocaleString()})`,
      ),
      min: 0,
      max: buildingTickStep * buildingTicks,
      tickAmount: buildingTicks,
      labels: {
        style: { colors: token.colorTextSecondary },
        formatter: (value: string) => `${Math.round(Number(value))}`,
      },
    },
    yaxis: {
      labels: {
        maxWidth: 240,
        style: { colors: token.colorTextSecondary, fontSize: '11px' },
      },
    },
  };

  const criticalSuppressed = summary.suppressed.filter(
    (event) => event.severity >= 5,
  ).length;
  const majorSuppressed = summary.suppressed.filter(
    (event) => event.severity === 4,
  ).length;

  // Group the agenda by day; overdue windows sit under today.
  const agendaDays: { label: string; items: MaintenanceWindow[] }[] = [];
  summary.agenda.forEach((win) => {
    const dayTime = Math.max(startTime(win), now);
    const label =
      new Date(dayTime).toDateString() === new Date(now).toDateString()
        ? `Today · ${formatDay(now)}`
        : formatDay(dayTime);
    const group = agendaDays.find((day) => day.label === label);
    if (group) {
      group.items.push(win);
    } else {
      agendaDays.push({ label, items: [win] });
    }
  });

  const timingNote = (win: MaintenanceWindow) => {
    const status = getWindowStatus(win, now);
    if (status === 'active') {
      return `${formatDuration(endTime(win) - now)} left`;
    }
    if (status === 'overdue') {
      return `${formatDuration(now - endTime(win))} past end`;
    }
    return `starts in ${formatDuration(startTime(win) - now)}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
        }}
      >
        <KpiCard
          label="Active now"
          value={summary.active.length}
          sub={`${summary.active.reduce((total, win) => total + (win.devices ?? []).length, 0)} devices · alerts suppressed`}
          valueColor={
            summary.active.length > 0 ? token.colorPrimary : undefined
          }
          onClick={() => onDrillDown('current')}
        />
        <KpiCard
          label="Awaiting approval"
          value={summary.awaiting.length}
          sub={
            summary.awaiting.length > 0
              ? `${summary.awaitingSoon.length} start within 24h · oldest ${formatDuration(now - summary.oldestRequest)}`
              : 'No bypass requests waiting'
          }
          valueColor={
            summary.awaiting.length > 0 ? token.colorWarning : undefined
          }
          onClick={() => onDrillDown('awaiting')}
        />
        <KpiCard
          label="Upcoming · 7 days"
          value={summary.upcoming.length}
          sub={`${summary.upcoming.filter((win) => win.details.requiresBypass).length} need a firewall bypass`}
          onClick={() => onDrillDown('current')}
        />
        <KpiCard
          label="Overlay bypasses open"
          value={summary.openOverlays.length}
          sub={
            summary.notRemoved.length > 0
              ? `${summary.notRemoved.length} past its window's end`
              : 'Removed automatically at close'
          }
          valueColor={
            summary.notRemoved.length > 0 ? token.colorError : undefined
          }
          onClick={() => onDrillDown('bypass')}
        />
        <KpiCard
          label={`Alerts suppressed · ${lookbackDays}d`}
          value={summary.suppressed.length.toLocaleString()}
          sub={`${criticalSuppressed} critical · ${majorSuppressed} major`}
        />
        <KpiCard
          label={`Closed · ${lookbackDays}d`}
          value={summary.closedHistory.length}
          sub={
            summary.closedHistory.length > 0
              ? `avg ${formatDuration(median(summary.closedHours) * HOUR_MS)} · ${Math.round(summary.deviceHours).toLocaleString()} device-hours`
              : 'None in this period'
          }
          onClick={() => onDrillDown('closed')}
        />
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title="Now & next 7 days"
          extra={
            <span style={{ fontSize: 12 }}>
              {summary.agenda.length} windows
            </span>
          }
          styles={cardStyles}
        >
          {agendaDays.length === 0 ? (
            <Empty description="Nothing running or scheduled in the next 7 days" />
          ) : (
            agendaDays.map((day) => (
              <div key={day.label}>
                <div style={{ ...sectionLabel, padding: '8px 0 4px' }}>
                  {day.label}
                </div>
                {day.items.map((win) => (
                  <div
                    key={win.id}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px 12px',
                      alignItems: 'center',
                      padding: '7px 0',
                      borderBottom: rowDivider,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ ...secondary, width: 104, flex: 'none' }}>
                      {formatTime(win.details.scheduledStartTime)} –{' '}
                      {formatTime(win.details.scheduledEndTime)}
                    </span>
                    <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <Link to={detailLink(win)}>{windowName(win)}</Link>
                      <span style={secondary}>
                        {' '}
                        · {getBuildingLabel(win)} · {(win.devices ?? []).length}{' '}
                        devices · {timingNote(win)}
                      </span>
                    </span>
                    <Space size={4} wrap>
                      <BypassTag state={getBypassState(win, now)} />
                      <StatusTag status={getWindowStatus(win, now)} />
                    </Space>
                  </div>
                ))}
              </div>
            ))
          )}
        </Card>

        <Card
          size="small"
          title="Needs attention"
          extra={
            <span style={{ fontSize: 12 }}>
              {summary.attention.length} items · most urgent first
            </span>
          }
          styles={cardStyles}
        >
          {summary.attention.length === 0 ? (
            <Empty description="Nothing needs attention" />
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {summary.attention.map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    gap: 10,
                    alignItems: 'start',
                    padding: '9px 0',
                    borderBottom: rowDivider,
                  }}
                >
                  <span style={{ paddingTop: 2 }}>
                    {severityIcon(item.severity)}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {item.title}
                    </div>
                    <div style={{ ...secondary, fontSize: 12, marginTop: 2 }}>
                      {item.detail}
                    </div>
                  </div>
                  {item.windowId && (
                    <Link
                      to={`/dashboard/maintenanceWindows2/${item.windowId}`}
                    >
                      <Button size="small">Open</Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={twoColumn}>
        <Card
          size="small"
          title="Overlay network bypasses"
          extra={
            <Button
              type="link"
              size="small"
              onClick={() => onDrillDown('bypass')}
            >
              View all
            </Button>
          }
          styles={cardStyles}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: 10,
              marginBottom: 12,
            }}
          >
            {[
              {
                label: `Requested · ${lookbackDays}d`,
                value: summary.bypassStats.requested,
              },
              { label: 'Overlays created', value: summary.bypassStats.created },
              {
                label: 'Not created',
                value: summary.bypassStats.notCreated,
                alert: summary.bypassStats.notCreated > 0,
              },
              { label: 'Rejected', value: summary.bypassStats.rejected },
              { label: 'Open now', value: summary.bypassStats.openNow },
            ].map((stat) => (
              <div key={stat.label}>
                <div style={sectionLabel}>{stat.label}</div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    marginTop: 3,
                    fontVariantNumeric: 'tabular-nums',
                    color: stat.alert ? token.colorError : undefined,
                  }}
                >
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: true }}
            dataSource={summary.bypassRows}
            rowKey={(win) => win.id}
            locale={{ emptyText: 'No firewall bypass requests' }}
            columns={[
              {
                title: 'Window',
                key: 'window',
                render: (_, win: MaintenanceWindow) => (
                  <>
                    <Link to={detailLink(win)}>{windowName(win)}</Link>
                    <div style={{ ...secondary, fontSize: 12 }}>
                      {getBuildingLabel(win)} · {formatDay(startTime(win))}
                    </div>
                  </>
                ),
              },
              {
                title: 'Overlay network',
                key: 'overlay',
                render: (_, win: MaintenanceWindow) => {
                  const overlays = win.overlayNetworkBypasses ?? [];
                  const controlSystems = getControlSystemNames(win);
                  return (
                    <>
                      {overlays.length > 0 ? (
                        <span style={{ fontSize: 12 }}>
                          {overlays
                            .map((overlay) => overlay.overlayNetworkName)
                            .join(', ')}
                        </span>
                      ) : (
                        <span style={secondary}>—</span>
                      )}
                      {controlSystems.length > 0 && (
                        <div style={{ ...secondary, fontSize: 12 }}>
                          {controlSystems.join(' · ')}
                        </div>
                      )}
                    </>
                  );
                },
              },
              {
                title: 'Lifecycle',
                key: 'lifecycle',
                render: (_, win: MaintenanceWindow) => lifecycleDots(win),
              },
              {
                title: 'State',
                key: 'state',
                render: (_, win: MaintenanceWindow) => (
                  <BypassTag state={getBypassState(win, now)} />
                ),
              },
            ]}
          />
          <div style={{ ...secondary, fontSize: 11, marginTop: 8 }}>
            Lifecycle: Requested → Approved → Overlay created at window start →
            Window closed (overlay removed)
          </div>
        </Card>

        <Card
          size="small"
          title="Approvals"
          extra={
            <span style={{ fontSize: 12 }}>
              Bypass requests · last {lookbackDays} days
            </span>
          }
          styles={cardStyles}
        >
          {outcomeTotal === 0 ? (
            <Empty description="No bypass requests in this period" />
          ) : (
            <>
              <div
                role="img"
                aria-label={outcomeParts
                  .map((part) => `${part.label} ${part.value}`)
                  .join(', ')}
                style={{
                  display: 'flex',
                  height: 16,
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: token.colorFillSecondary,
                }}
              >
                {outcomeParts
                  .filter((part) => part.value > 0)
                  .map((part) => (
                    <span
                      key={part.key}
                      style={{
                        width: `${(part.value / outcomeTotal) * 100}%`,
                        background: part.color,
                      }}
                    />
                  ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 14px',
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                {outcomeParts.map((part) => (
                  <span key={part.key}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: part.color,
                        marginRight: 6,
                      }}
                    />
                    {part.label} {part.value}
                  </span>
                ))}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 10,
                  margin: '14px 0',
                }}
              >
                <div>
                  <div style={sectionLabel}>Median turnaround</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 3 }}>
                    {formatDuration(median(summary.responseTimes))}
                  </div>
                </div>
                <div>
                  <div style={sectionLabel}>90th percentile</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 3 }}>
                    {formatDuration(percentile(summary.responseTimes, 0.9))}
                  </div>
                </div>
                <div>
                  <Tooltip title="Bypass approved less than 24 hours before the window started">
                    <div style={sectionLabel}>
                      Approved &lt; 24h before start
                    </div>
                  </Tooltip>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 3 }}>
                    {summary.lateApprovals}
                  </div>
                </div>
              </div>
              <Table
                size="small"
                pagination={false}
                dataSource={summary.approverRows}
                rowKey={(row) => row.approver}
                locale={{
                  emptyText: 'No manual approval decisions in this period',
                }}
                columns={[
                  {
                    title: 'Approver',
                    dataIndex: 'approver',
                    key: 'approver',
                    ellipsis: true,
                  },
                  {
                    title: 'Pending',
                    dataIndex: 'pending',
                    key: 'pending',
                    align: 'right' as const,
                    render: (value: number) =>
                      value > 0 ? (
                        <span
                          style={{
                            color: token.colorWarningText,
                            fontWeight: 600,
                          }}
                        >
                          {value}
                        </span>
                      ) : (
                        value
                      ),
                  },
                  {
                    title: 'Median',
                    dataIndex: 'median',
                    key: 'median',
                    align: 'right' as const,
                    render: (value: number) => formatDuration(value),
                  },
                  {
                    title: 'Approved / rejected',
                    key: 'decided',
                    align: 'right' as const,
                    render: (_, row) => `${row.approved} / ${row.rejected}`,
                  },
                ]}
              />
              <div style={{ ...secondary, fontSize: 11, marginTop: 8 }}>
                Any one approver approves a bypass. Auto-approvals (the creator
                is an approver) are excluded from turnaround.
              </div>
            </>
          )}
        </Card>
      </div>

      {summary.myDecisions.length > 0 && (
        <Card
          size="small"
          title="Awaiting your decision"
          extra={
            <span style={{ fontSize: 12 }}>
              Bypass requests where you are an approver · soonest first
            </span>
          }
          styles={cardStyles}
        >
          <Table
            size="small"
            pagination={false}
            scroll={{ x: true }}
            dataSource={summary.myDecisions}
            rowKey={(win) => win.id}
            columns={[
              {
                title: 'Window',
                key: 'window',
                render: (_, win: MaintenanceWindow) => (
                  <Link to={detailLink(win)}>{windowName(win)}</Link>
                ),
              },
              {
                title: 'Building',
                key: 'building',
                render: (_, win: MaintenanceWindow) => getBuildingLabel(win),
              },
              {
                title: 'Starts',
                key: 'starts',
                render: (_, win: MaintenanceWindow) => {
                  const startsIn = startTime(win) - now;
                  return (
                    <>
                      <span
                        style={{
                          color:
                            startsIn < DAY_MS
                              ? token.colorWarningText
                              : undefined,
                          fontWeight: startsIn < DAY_MS ? 600 : undefined,
                        }}
                      >
                        in {formatDuration(startsIn)}
                      </span>
                      <div style={{ ...secondary, fontSize: 12 }}>
                        {formatDay(startTime(win))}{' '}
                        {formatTime(win.details.scheduledStartTime)} –{' '}
                        {formatTime(win.details.scheduledEndTime)}
                      </div>
                    </>
                  );
                },
              },
              {
                title: 'Devices',
                key: 'devices',
                align: 'right' as const,
                render: (_, win: MaintenanceWindow) =>
                  (win.devices ?? []).length,
              },
              {
                title: 'Requested by',
                dataIndex: 'createdBy',
                key: 'createdBy',
              },
              {
                title: 'Waiting',
                key: 'waiting',
                render: (_, win: MaintenanceWindow) =>
                  formatDuration(
                    now -
                      Math.min(
                        ...(win.approvals ?? []).map((approval) =>
                          toTime(approval.requestedAt),
                        ),
                      ),
                  ),
              },
              {
                title: 'Decision',
                key: 'decision',
                align: 'right' as const,
                render: (_, win: MaintenanceWindow) => (
                  <Space>
                    <Button
                      size="small"
                      danger
                      disabled={sending}
                      onClick={() => onAction('reject', win)}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      disabled={sending}
                      onClick={() => onAction('approve', win)}
                    >
                      Approve
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      <div style={twoColumn}>
        <Card
          size="small"
          title="Windows by month"
          extra={
            <span style={{ fontSize: 12 }}>By firewall bypass outcome</span>
          }
          styles={cardStyles}
        >
          {summary.months.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={260}
              options={monthlyOptions}
              series={[
                {
                  name: 'Bypass approved',
                  data: summary.monthly.bypassApproved,
                },
                { name: 'No bypass', data: summary.monthly.noBypass },
                {
                  name: 'Bypass not approved',
                  data: summary.monthly.bypassNotApproved,
                },
              ]}
            />
          ) : (
            <Empty />
          )}
        </Card>
        <Card
          size="small"
          title="Maintenance load by building"
          extra={
            <span style={{ fontSize: 12 }}>
              Windows · device-hours in brackets · click to filter
            </span>
          }
          styles={cardStyles}
        >
          {summary.buildings.length > 0 ? (
            <ReactApexChart
              type="bar"
              height={Math.max(200, summary.buildings.length * 30 + 50)}
              options={buildingOptions}
              series={[
                {
                  name: 'Windows',
                  data: summary.buildings.map((building) => building.windows),
                },
              ]}
            />
          ) : (
            <Empty
              description={`No windows in the last ${lookbackDays} days`}
            />
          )}
        </Card>
      </div>

      <div style={{ ...secondary, fontSize: 12, textAlign: 'right' }}>
        Updated {new Date(now).toLocaleString()}
      </div>
    </div>
  );
};

export default SummaryTab;
